import { Head, Link, router } from "@inertiajs/react"
import { useState, useEffect, useCallback } from "react"
import { toast } from "sonner"
import { track } from "@/lib/analytics"
import type { Video, Script, Listing, Voice, Account, SceneAsset, Render, SocialConnection } from "@/types"
import cable from "@/lib/cable"
import { csrfToken } from "@/lib/csrf"
import DashboardLayout from "@/components/dashboard/layout"
import WizardTimeline, { STEPS, type WizardStep } from "@/components/dashboard/videos/wizard-timeline"
import WizardStepStyle from "@/components/dashboard/videos/wizard-step-style"
import WizardStepScript from "@/components/dashboard/videos/wizard-step-script"
import WizardStepAudio from "@/components/dashboard/videos/wizard-step-audio"
import WizardStepStoryboard from "@/components/dashboard/videos/wizard-step-storyboard"
import WizardStepRender from "@/components/dashboard/videos/wizard-step-render"
import VideoCompletedView from "@/components/dashboard/videos/video-completed-view"
import { useRenderChannel } from "@/hooks/use-render-channel"
import { useSocialPostChannel } from "@/hooks/use-social-post-channel"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import VideoActionsMenu from "@/components/dashboard/videos/video-actions-menu"

const FORMAT_LABELS: Record<Video["aspect_ratio"], string> = {
  portrait: "9:16 Portrait",
  landscape: "16:9 Landscape",
  square: "1:1 Square",
}

const STATUS_STYLES: Record<Video["status"], string> = {
  draft: "bg-stone/60 text-slate",
  scripted: "bg-stone/60 text-slate",
  rendered: "bg-charcoal text-white",
  published: "bg-red text-white",
  archived: "bg-warm-grey/20 text-warm-grey",
}

type SceneAssetDetail = {
  scene_index: number
  asset_type: "enhanced_image" | "animated_clip"
  scene_asset_id: number
  message?: string
  progress?: number
}

type VideoChannelMessage = {
  event: "script_progress" | "voiceover_progress" | "music_progress" | "scene_asset_progress" | "render_progress"
  status: string
  detail: string | SceneAssetDetail
}

export type VoiceRegion = "british" | "north_america"

export default function ShowVideo({
  video,
  scripts,
  listing,
  voices_by_region,
  default_voice_region,
  account,
  current_role,
  renders,
  connections,
}: {
  video: Video
  scripts: Script[]
  listing: Listing
  voices_by_region: Record<VoiceRegion, Voice[]>
  default_voice_region: VoiceRegion
  account: Account
  current_role: string
  renders: Render[]
  connections: SocialConnection[]
}) {
  const hasCompletedRender = renders.some((r) => r.status === "completed")
  const isCompleted = (video.status === "rendered" || video.status === "published") && hasCompletedRender
  const [isEditMode, setIsEditMode] = useState(!isCompleted)

  const [currentStep, setCurrentStep] = useState<WizardStep>(() => {
    const step = video.wizard_step
    if (step === "photos") return "style"
    if (step === "branding" || step === "review") return "render"
    return (step as WizardStep) || "style"
  })
  const [isSaving, setIsSaving] = useState(false)

  // --- Generation states (initialised from server, updated by WebSocket + polling) ---
  const activeScriptInit = scripts.find((s) => s.active)
  const [scriptGenerating, setScriptGenerating] = useState(
    () => video.script_status === "generating",
  )
  const [scriptMessage, setScriptMessage] = useState(
    () => video.script_status === "generating" ? "Generating script..." : "",
  )
  const [voiceoverGenerating, setVoiceoverGenerating] = useState(
    () => activeScriptInit?.voiceover_status === "generating",
  )
  const [voiceoverMessage, setVoiceoverMessage] = useState(
    () => activeScriptInit?.voiceover_status === "generating" ? "Generating voiceover..." : "",
  )
  const [musicGenerating, setMusicGenerating] = useState(
    () => video.music_status === "generating",
  )
  const [musicMessage, setMusicMessage] = useState(
    () => video.music_status === "generating" ? "Generating music..." : "",
  )

  // Scene asset statuses keyed by "sceneIndex-assetType"
  const [sceneAssetStatuses, setSceneAssetStatuses] = useState<
    Record<string, { status: SceneAsset["status"]; message?: string; progress?: number }>
  >(() => {
    const map: Record<string, { status: SceneAsset["status"]; message?: string; progress?: number }> = {}
    for (const sa of video.scene_assets) {
      map[`${sa.scene_index}-${sa.asset_type}`] = { status: sa.status, message: sa.error_message }
    }
    return map
  })

  // --- WebSocket subscription ---
  useEffect(() => {
    const subscription = cable.subscriptions.create(
      { channel: "VideoChannel", id: video.id },
      {
        received(data: VideoChannelMessage) {
          if (data.event === "script_progress") {
            if (data.status === "generating") {
              setScriptGenerating(true)
              setScriptMessage(data.detail as string)
            } else {
              setScriptGenerating(false)
              setScriptMessage("")
              router.reload()
            }
          }

          if (data.event === "voiceover_progress") {
            if (data.status === "generating") {
              setVoiceoverGenerating(true)
              setVoiceoverMessage(data.detail as string)
            } else {
              setVoiceoverGenerating(false)
              setVoiceoverMessage("")
              router.reload()
            }
          }

          if (data.event === "music_progress") {
            if (data.status === "generating") {
              setMusicGenerating(true)
              setMusicMessage(data.detail as string)
            } else {
              setMusicGenerating(false)
              setMusicMessage("")
              router.reload()
            }
          }

          if (data.event === "scene_asset_progress") {
            const detail = data.detail as SceneAssetDetail
            const key = `${detail.scene_index}-${detail.asset_type}`
            if (data.status === "generating") {
              setSceneAssetStatuses((prev) => ({
                ...prev,
                [key]: { status: "generating", message: detail.message, progress: detail.progress },
              }))
            } else {
              setSceneAssetStatuses((prev) => ({
                ...prev,
                [key]: { status: data.status as SceneAsset["status"], message: detail.message },
              }))
              router.reload()
            }
          }
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [video.id])

  // --- Polling fallback: reload server props every 3s while any generation is active ---
  const anySceneAssetGenerating = Object.values(sceneAssetStatuses).some(
    (s) => s.status === "generating",
  )
  const isGenerating = scriptGenerating || voiceoverGenerating || musicGenerating || anySceneAssetGenerating
  useEffect(() => {
    if (!isGenerating) return
    const interval = setInterval(() => {
      router.reload()
    }, 3000)
    return () => clearInterval(interval)
  }, [isGenerating])

  // --- Prop-syncing: reconcile server state with local generating state ---
  // Uses React's "adjust state when a prop changes" pattern (set state during render)
  const [prevScriptStatus, setPrevScriptStatus] = useState(video.script_status)
  if (video.script_status !== prevScriptStatus) {
    setPrevScriptStatus(video.script_status)
    if (video.script_status === "completed" || video.script_status === "failed") {
      setScriptGenerating(false)
      setScriptMessage("")
    } else if (video.script_status === "generating") {
      setScriptGenerating(true)
      setScriptMessage("Generating script...")
    }
  }

  const currentVoiceoverStatus = scripts.find((s) => s.active)?.voiceover_status
  const [prevVoiceoverStatus, setPrevVoiceoverStatus] = useState(currentVoiceoverStatus)
  if (currentVoiceoverStatus !== prevVoiceoverStatus) {
    setPrevVoiceoverStatus(currentVoiceoverStatus)
    if (currentVoiceoverStatus === "completed" || currentVoiceoverStatus === "failed") {
      setVoiceoverGenerating(false)
      setVoiceoverMessage("")
    } else if (currentVoiceoverStatus === "generating") {
      setVoiceoverGenerating(true)
      setVoiceoverMessage("Generating voiceover...")
    }
  }

  const [prevMusicStatus, setPrevMusicStatus] = useState(video.music_status)
  if (video.music_status !== prevMusicStatus) {
    setPrevMusicStatus(video.music_status)
    if (video.music_status === "completed" || video.music_status === "failed") {
      setMusicGenerating(false)
      setMusicMessage("")
    } else if (video.music_status === "generating") {
      setMusicGenerating(true)
      setMusicMessage("Generating music...")
    }
  }

  // Sync scene asset statuses from server props on reload
  const [prevSceneAssets, setPrevSceneAssets] = useState(video.scene_assets)
  if (video.scene_assets !== prevSceneAssets) {
    setPrevSceneAssets(video.scene_assets)
    const map: Record<string, { status: SceneAsset["status"]; message?: string; progress?: number }> = {}
    for (const sa of video.scene_assets) {
      map[`${sa.scene_index}-${sa.asset_type}`] = { status: sa.status, message: sa.error_message }
    }
    setSceneAssetStatuses(map)
  }

  // --- Local state for wizard data (synced to server on Next) ---
  const [wizardData, setWizardData] = useState({
    name: video.name,
    aspect_ratio: video.aspect_ratio,
    template_variant: video.template_variant,
    transition_style: video.transition_style,
    target_length: video.target_length,
    selected_images: video.selected_images || [],
    target_audience: video.target_audience || "",
    tone: video.tone,
    language: video.language,
    notes: video.notes || "",
    voice: video.voice || "",
    voice_speed: video.voice_speed ?? 1.0,
    voice_style: video.voice_style ?? 0.0,
    voiceover_volume: video.voiceover_volume ?? 1.0,
    cta_override: video.cta_override || "Book a viewing",
    show_logo: video.show_logo,
    music_prompt: video.music_prompt || "",
    music_volume: video.music_volume ?? 0.3,
  })

  const currentIndex = STEPS.findIndex((s) => s.key === currentStep)
  const isFirst = currentIndex === 0
  const isLast = currentIndex === STEPS.length - 1
  const hasActiveScript = scripts.some((s) => s.active)
  const canProceed = currentStep !== "script" || (hasActiveScript && !scriptGenerating)

  function updateWizardData(updates: Partial<typeof wizardData>) {
    setWizardData((prev) => ({ ...prev, ...updates }))
  }

  function saveAndNavigate(targetStep: WizardStep) {
    setIsSaving(true)
    track("video_wizard_step_advanced", {
      video_id: video.id,
      from_step: currentStep,
      to_step: targetStep,
    })
    router.patch(
      `/dashboard/videos/${video.id}`,
      {
        video: {
          ...wizardData,
          wizard_step: targetStep,
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => setCurrentStep(targetStep),
        onFinish: () => setIsSaving(false),
      },
    )
  }

  function handleNext() {
    if (isLast) return
    const nextStep = STEPS[currentIndex + 1].key
    saveAndNavigate(nextStep)
  }

  function handleBack() {
    if (isFirst) return
    const prevStep = STEPS[currentIndex - 1].key
    saveAndNavigate(prevStep)
  }

  function handleStepClick(step: WizardStep) {
    if (step === currentStep) return
    saveAndNavigate(step)
  }

  // --- Render state ---
  const [isRendering, setIsRendering] = useState(false)
  const [activeRenderId, setActiveRenderId] = useState<string | null>(null)
  const [renderError, setRenderError] = useState<string | null>(null)

  const handleRenderComplete = useCallback(() => {
    setIsRendering(false)
    setActiveRenderId(null)
    track("video_render_completed", { video_id: video.id })
    router.reload()
  }, [video.id])

  const { renderProgress } = useRenderChannel(video.id, activeRenderId, handleRenderComplete)

  // Real-time social post status updates via ActionCable + polling fallback
  const inflightRenderIds = renders
    .filter((r) => r.social_posts?.some((p) => p.status === "pending" || p.status === "uploading"))
    .map((r) => r.id)
  useSocialPostChannel(inflightRenderIds, inflightRenderIds.length > 0)

  async function handleRender() {
    setIsRendering(true)
    setRenderError(null)
    track("video_render_started", { video_id: video.id })

    try {
      const res = await fetch(`/dashboard/videos/${video.id}/renders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
      })

      if (res.ok) {
        const data = await res.json()
        setActiveRenderId(data.render_id)
      } else {
        const data = await res.json().catch(() => ({}))
        setRenderError(data.error || "Failed to start render")
        setIsRendering(false)
      }
    } catch {
      setRenderError("Failed to start render")
      setIsRendering(false)
    }
  }

  // --- Generate callbacks (plain fetch, not Inertia router) ---
  // Using fetch avoids the Inertia redirect cycle (POST → 302 → GET → re-render)
  // which flashes stale server state. WebSocket handles all progress updates.
  async function fireAction(url: string, body: Record<string, unknown> = {}): Promise<boolean> {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        toast.error(data.error || "Something went wrong. Please try again.")
      }
      return res.ok
    } catch {
      toast.error("Something went wrong. Please try again.")
      return false
    }
  }

  async function handleGenerateScript(regenerateNotes?: string) {
    setScriptGenerating(true)
    setScriptMessage("Queuing script generation...")
    track("video_script_generated", { video_id: video.id })
    const ok = await fireAction(`/dashboard/videos/${video.id}/generate_script`, {
      regenerate_notes: regenerateNotes || undefined,
      video: {
        target_audience: wizardData.target_audience,
        tone: wizardData.tone,
        language: wizardData.language,
        notes: wizardData.notes,
      },
    })
    if (!ok) {
      setScriptGenerating(false)
      setScriptMessage("")
      router.reload()
    }
  }

  async function handleGenerateVoiceover() {
    const script = scripts.find((s) => s.active)
    if (!script) return
    setVoiceoverGenerating(true)
    setVoiceoverMessage("Queuing voiceover generation...")
    track("video_voiceover_generated", { video_id: video.id })
    const ok = await fireAction(
      `/dashboard/videos/${video.id}/scripts/${script.id}/generate_voiceover`,
      {
        voice: wizardData.voice,
        voice_speed: wizardData.voice_speed,
        voice_style: wizardData.voice_style,
      },
    )
    if (!ok) {
      setVoiceoverGenerating(false)
      setVoiceoverMessage("")
      router.reload()
    }
  }

  async function handleGenerateMusic() {
    setMusicGenerating(true)
    setMusicMessage("Queuing music generation...")
    track("video_music_generated", { video_id: video.id })
    const ok = await fireAction(`/dashboard/videos/${video.id}/generate_music`)
    if (!ok) {
      setMusicGenerating(false)
      setMusicMessage("")
      router.reload()
    }
  }

  function handleDeleteVoiceover() {
    const script = scripts.find((s) => s.active)
    if (!script) return
    router.delete(
      `/dashboard/videos/${video.id}/scripts/${script.id}/delete_voiceover`,
      { preserveScroll: true },
    )
  }

  function handleDeleteMusic() {
    router.delete(
      `/dashboard/videos/${video.id}/delete_music`,
      { preserveScroll: true },
    )
  }

  async function handleEnhanceScene(sceneIndex: number) {
    const key = `${sceneIndex}-enhanced_image`
    track("video_scene_enhanced", { video_id: video.id, scene_index: sceneIndex })
    setSceneAssetStatuses((prev) => ({
      ...prev,
      [key]: { status: "generating" },
    }))
    const ok = await fireAction(`/dashboard/videos/${video.id}/scenes/${sceneIndex}/enhance`)
    if (!ok) {
      setSceneAssetStatuses((prev) => ({
        ...prev,
        [key]: { status: "failed" },
      }))
      router.reload()
    }
  }

  async function handleAnimateScene(sceneIndex: number, motionPrompt?: string) {
    const key = `${sceneIndex}-animated_clip`
    track("video_scene_animated", { video_id: video.id, scene_index: sceneIndex })
    setSceneAssetStatuses((prev) => ({
      ...prev,
      [key]: { status: "generating" },
    }))
    const body: Record<string, unknown> = {}
    if (motionPrompt) body.motion_prompt = motionPrompt
    const ok = await fireAction(`/dashboard/videos/${video.id}/scenes/${sceneIndex}/animate`, body)
    if (!ok) {
      setSceneAssetStatuses((prev) => ({
        ...prev,
        [key]: { status: "failed" },
      }))
      router.reload()
    }
  }

  async function handleRevertScene(sceneIndex: number) {
    try {
      const res = await fetch(`/dashboard/videos/${video.id}/scenes/${sceneIndex}/revert`, {
        method: "DELETE",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
      })
      if (res.ok) {
        // Clear local statuses for this scene
        setSceneAssetStatuses((prev) => {
          const next = { ...prev }
          delete next[`${sceneIndex}-enhanced_image`]
          delete next[`${sceneIndex}-animated_clip`]
          return next
        })
        router.reload()
      }
    } catch {
      // Silently fail
    }
  }

  function handleUpdateScript(content: Record<string, unknown>) {
    const script = scripts.find((s) => s.active)
    if (!script) return
    router.patch(
      `/dashboard/videos/${video.id}/scripts/${script.id}`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { script: { content } } as any,
      { preserveScroll: true },
    )
  }

  // --- Completed view mode ---
  if (!isEditMode && isCompleted) {
    return (
      <DashboardLayout>
        <Head title={`${video.name} | QuiteRight`} />

        {/* Breadcrumb */}
        <Link
          href={`/dashboard/listings/${listing.slug}`}
          className="mb-6 inline-flex items-center gap-1 text-sm text-warm-grey hover:text-charcoal"
        >
          <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          {listing.address}
        </Link>

        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal">
              {video.name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Badge className={`capitalize ${STATUS_STYLES[video.status]}`}>
                {video.status}
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {FORMAT_LABELS[video.aspect_ratio]}
              </Badge>
              <Badge variant="outline" className="text-[10px] capitalize">
                {video.template_variant}
              </Badge>
              <Badge variant="outline" className="text-[10px] capitalize">
                {video.transition_style}
              </Badge>
              <Badge variant="outline" className="text-[10px]">
                {video.target_length}s
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setIsEditMode(true)
                setCurrentStep("storyboard")
              }}
            >
              <svg className="mr-1 size-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
              </svg>
              Edit video
            </Button>
            <VideoActionsMenu video={video} currentRole={current_role} />
          </div>
        </div>

        <VideoCompletedView
          video={video}
          listing={listing}
          renders={renders}
          connections={connections}
          scripts={scripts}
          onRender={handleRender}
          isRendering={isRendering}
          renderProgress={activeRenderId ? renderProgress : undefined}
          renderError={renderError}
        />
      </DashboardLayout>
    )
  }

  // --- Wizard mode ---
  return (
    <DashboardLayout>
      <Head title={`${video.name} | QuiteRight`} />

      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link
            href={`/dashboard/listings/${listing.slug}`}
            className="inline-flex items-center gap-1 text-sm text-warm-grey hover:text-charcoal"
          >
            <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
            {listing.address}
          </Link>
          <span className="text-xs text-warm-grey">
            Updated {formatRelativeTime(video.updated_at)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isCompleted && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditMode(false)}
            >
              <svg className="mr-1 size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
              </svg>
              Back to video
            </Button>
          )}
          <VideoActionsMenu video={video} currentRole={current_role} />
        </div>
      </div>

      {/* Timeline */}
      <WizardTimeline currentStep={currentStep} onStepClick={handleStepClick} />

      {/* Step content */}
      <div className={`mx-auto ${currentStep === "storyboard" ? "max-w-5xl" : "max-w-3xl"}`}>
        <div className={`rounded-xl border border-stone/40 bg-white ${currentStep === "storyboard" ? "p-4 sm:p-5" : "p-6 sm:p-8"}`}>
          {currentStep === "style" && (
            <WizardStepStyle
              data={{
                name: wizardData.name,
                aspect_ratio: wizardData.aspect_ratio,
                template_variant: wizardData.template_variant,
                transition_style: wizardData.transition_style,
                target_length: wizardData.target_length,
              }}
              onChange={updateWizardData}
              listing={listing}
              selectedImages={wizardData.selected_images}
              onImagesChange={(images) => updateWizardData({ selected_images: images })}
            />
          )}

          {currentStep === "script" && (
            <WizardStepScript
              scripts={scripts}
              data={{
                target_audience: wizardData.target_audience,
                tone: wizardData.tone,
                language: wizardData.language,
                notes: wizardData.notes,
              }}
              onChange={updateWizardData}
              isGenerating={scriptGenerating}
              generatingMessage={scriptMessage}
              scriptFailed={video.script_status === "failed"}
              onGenerate={handleGenerateScript}
              onUpdateScript={handleUpdateScript}
            />
          )}

          {currentStep === "audio" && (
            <WizardStepAudio
              video={video}
              scripts={scripts}
              voicesByRegion={voices_by_region}
              defaultVoiceRegion={default_voice_region}
              selectedVoice={wizardData.voice}
              voiceSpeed={wizardData.voice_speed}
              voiceStyle={wizardData.voice_style}
              voiceoverVolume={wizardData.voiceover_volume}
              onVoiceChange={(voiceId) => updateWizardData({ voice: voiceId })}
              onVoiceSpeedChange={(speed) => updateWizardData({ voice_speed: speed })}
              onVoiceStyleChange={(style) => updateWizardData({ voice_style: style })}
              onVoiceoverVolumeChange={(volume) => updateWizardData({ voiceover_volume: volume })}
              onMusicPromptChange={(prompt) => updateWizardData({ music_prompt: prompt })}
              musicVolume={wizardData.music_volume}
              onMusicVolumeChange={(volume) => updateWizardData({ music_volume: volume })}
              voiceoverGenerating={voiceoverGenerating}
              voiceoverMessage={voiceoverMessage}
              onGenerateVoiceover={handleGenerateVoiceover}
              onDeleteVoiceover={handleDeleteVoiceover}
              musicGenerating={musicGenerating}
              musicMessage={musicMessage}
              onGenerateMusic={handleGenerateMusic}
              onDeleteMusic={handleDeleteMusic}
            />
          )}

          {currentStep === "storyboard" && (
            <WizardStepStoryboard
              scripts={scripts}
              aspectRatio={wizardData.aspect_ratio}
              templateVariant={wizardData.template_variant}
              imageUrls={wizardData.selected_images.map((sid) => {
                const match = listing.images_data?.find((img) => img.signed_id === sid)
                return match?.url || ""
              }).filter(Boolean)}
              allImagesData={listing.images_data || []}
              onSwapSceneImage={(sceneIndex, signedId) => {
                const images = [...wizardData.selected_images]
                if (images[sceneIndex] === signedId) return
                images[sceneIndex] = signedId
                updateWizardData({ selected_images: images })
                const hasAssets = sceneAssetStatuses[`${sceneIndex}-enhanced_image`] || sceneAssetStatuses[`${sceneIndex}-animated_clip`]
                if (hasAssets) handleRevertScene(sceneIndex)
              }}
              onUpdateScript={handleUpdateScript}
              sceneAssets={video.scene_assets}
              sceneAssetStatuses={sceneAssetStatuses}
              onEnhanceScene={handleEnhanceScene}
              onAnimateScene={handleAnimateScene}
              onRevertScene={handleRevertScene}
              voiceoverCompleted={scripts.find((s) => s.active)?.voiceover_status === "completed"}
              hasMusic={video.music_status === "completed"}
              musicUrl={video.generated_music_url}
              musicVolume={wizardData.music_volume}
              onMusicVolumeChange={(volume) => updateWizardData({ music_volume: volume })}
              voiceoverVolume={wizardData.voiceover_volume}
              onVoiceoverVolumeChange={(volume) => updateWizardData({ voiceover_volume: volume })}
              listing={listing}
              account={account}
            />
          )}

          {currentStep === "render" && (
            <WizardStepRender
              video={video}
              scripts={scripts}
              renders={renders}
              connections={connections}
              onRender={handleRender}
              onGoToStoryboard={() => handleStepClick("storyboard")}
              isRendering={isRendering}
              renderProgress={activeRenderId ? renderProgress : undefined}
              renderError={renderError}
            />
          )}

          {/* Navigation footer */}
          <div className="mt-8 flex items-center justify-between border-t border-stone/30 pt-6">
            {!isFirst ? (
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={isSaving}
              >
                <svg className="mr-1 size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                </svg>
                Back
              </Button>
            ) : (
              <div />
            )}

            {!isLast ? (
              <Button
                onClick={handleNext}
                disabled={isSaving || !canProceed}
                className="bg-red text-white hover:bg-deep-red"
              >
                {isSaving ? "Saving..." : "Next"}
                <svg className="ml-1 size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </Button>
            ) : (
              <div />
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}

function formatRelativeTime(dateStr: string | Date): string {
  const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
}
